import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Package, 
  ShoppingCart, 
  Warehouse, 
  TrendingUp,
  AlertTriangle,
  Truck,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ClipboardCheck,
  Clock,
  User,
  MapPin,
  ChevronRight,
  FileText,
  Download,
  MessageSquare,
  Send,
  Calendar,
  Bell,
  Play,
  Pause,
  Trash2,
  Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const NASDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    pendingOrders: 0,
    bodegaStock: 0,
    lowStockItems: 0,
    todaySales: 0,
    pendingDeliveries: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingCustomerOrders, setPendingCustomerOrders] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState({ count: 0, criticalCount: 0, items: [] });
  const [shopLowStockAlerts, setShopLowStockAlerts] = useState({ count: 0, criticalCount: 0, items: [] });
  const [reorderSuggestions, setReorderSuggestions] = useState({ count: 0, urgentCount: 0, suggestions: [] });
  
  // Scheduled alerts state
  const [scheduledAlerts, setScheduledAlerts] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    alert_type: 'low_stock',
    schedule_time: '06:00',
    days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    is_active: true
  });
  
  useEffect(() => {
    loadDashboardData();
    loadScheduledAlerts();
  }, []);
  
  const loadScheduledAlerts = async () => {
    try {
      const response = await axios.get(`${API}/api/nas/alerts/scheduled`);
      setScheduledAlerts(response.data);
    } catch (err) {
      console.log('Could not load scheduled alerts');
    }
  };
  
  const createScheduledAlert = async () => {
    try {
      await axios.post(`${API}/api/nas/alerts/scheduled`, newSchedule);
      toast.success('Scheduled alert created');
      setShowScheduleModal(false);
      setNewSchedule({
        alert_type: 'low_stock',
        schedule_time: '06:00',
        days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
        is_active: true
      });
      loadScheduledAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create scheduled alert');
    }
  };
  
  const toggleScheduledAlert = async (alertId, isActive) => {
    try {
      await axios.put(`${API}/api/nas/alerts/scheduled/${alertId}`, { is_active: !isActive });
      toast.success(`Alert ${isActive ? 'paused' : 'activated'}`);
      loadScheduledAlerts();
    } catch (err) {
      toast.error('Failed to update alert');
    }
  };
  
  const deleteScheduledAlert = async (alertId) => {
    if (!window.confirm('Delete this scheduled alert?')) return;
    try {
      await axios.delete(`${API}/api/nas/alerts/scheduled/${alertId}`);
      toast.success('Alert deleted');
      loadScheduledAlerts();
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };
  
  const runAlertNow = async (alertId) => {
    try {
      const response = await axios.post(`${API}/api/nas/alerts/scheduled/${alertId}/run`);
      toast.success(`Alert sent to ${response.data.sent} recipients`);
    } catch (err) {
      toast.error('Failed to run alert');
    }
  };
  
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      if (navigator.onLine) {
        const response = await axios.get(`${API}/api/nas/dashboard/stats`);
        setStats(response.data);
        localStorage.setItem('nas_dashboard_stats', JSON.stringify(response.data));
      } else {
        // Offline - use cached data
        const cachedStats = localStorage.getItem('nas_dashboard_stats');
        if (cachedStats) {
          setStats(JSON.parse(cachedStats));
          toast.info('Offline mode - showing cached data');
        }
      }
      
      // Load recent activity from sales
      try {
        const salesResponse = await axios.get(`${API}/api/nas/shop/sales`);
        const sales = salesResponse.data.slice(0, 5);
        const activities = sales.map(sale => ({
          type: 'sale',
          message: `${sale.type === 'wholesale' ? 'Wholesale' : 'Retail'} sale: ${sale.quantity} ${sale.type === 'wholesale' ? 'sacks' : 'units'} - ${sale.productName}`,
          time: new Date(sale.timestamp).toLocaleString(),
          icon: TrendingUp
        }));
        setRecentActivity(activities);
      } catch (err) {
        console.log('Could not load recent activity');
      }
      
      // Load pending customer orders for approval widget
      try {
        const ordersResponse = await axios.get(`${API}/api/nas-customer/admin/pending-orders`);
        setPendingCustomerOrders(ordersResponse.data.slice(0, 5)); // Show max 5
      } catch (err) {
        console.log('Could not load pending customer orders');
        setPendingCustomerOrders([]);
      }
      
      // Load low stock alerts (Bodega)
      try {
        const alertsResponse = await axios.get(`${API}/api/nas/alerts/low-stock/bodega`);
        setLowStockAlerts(alertsResponse.data);
      } catch (err) {
        console.log('Could not load low stock alerts');
      }
      
      // Load low stock alerts (Shop)
      try {
        const shopAlertsResponse = await axios.get(`${API}/api/nas/alerts/low-stock/shop`);
        setShopLowStockAlerts(shopAlertsResponse.data);
      } catch (err) {
        console.log('Could not load shop low stock alerts');
      }
      
      // Load reorder suggestions
      try {
        const reorderResponse = await axios.get(`${API}/api/nas/alerts/reorder-suggestions`);
        setReorderSuggestions(reorderResponse.data);
      } catch (err) {
        console.log('Could not load reorder suggestions');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Try cached data
      const cachedStats = localStorage.getItem('nas_dashboard_stats');
      if (cachedStats) {
        setStats(JSON.parse(cachedStats));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return `₱${(amount || 0).toLocaleString()}`;
  };
  
  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500">Nenen's Agrivet Supply Overview</p>
        </div>
        <Button variant="outline" onClick={loadDashboardData} disabled={isLoading}>
          <RefreshCw size={18} className={isLoading ? 'animate-spin mr-2' : 'mr-2'} />
          Refresh
        </Button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/products')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Products</p>
                <p className="text-2xl font-bold text-slate-800">{stats.totalProducts}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Package className="text-blue-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/orders')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Orders</p>
                <p className="text-2xl font-bold text-slate-800">{stats.pendingOrders}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <ShoppingCart className="text-amber-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/bodega')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Bodega Stock</p>
                <p className="text-2xl font-bold text-slate-800">{(stats.bodegaStock || 0).toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Warehouse className="text-green-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/products')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-red-600">{stats.lowStockItems}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/shop')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Today's Sales</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.todaySales)}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/nas/deliveries')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Deliveries</p>
                <p className="text-2xl font-bold text-slate-800">{stats.pendingDeliveries}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Truck className="text-purple-600" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Quick Actions - Customer Portal Link */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Order on Behalf of Customer</h3>
                <p className="text-sm text-slate-500">Access customer portal to place orders for customers</p>
              </div>
            </div>
            <Button 
              onClick={() => window.open('/nas-portal/login', '_blank')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="customer-portal-link"
            >
              <User size={16} className="mr-2" />
              Open Customer Portal
              <ArrowUpRight size={16} className="ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Pending Customer Orders for Approval */}
      {pendingCustomerOrders.length > 0 && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="text-amber-600" size={20} />
                Orders Awaiting Approval
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 ml-2">
                  {pendingCustomerOrders.length} pending
                </Badge>
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/nas/order-approval')}
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
              >
                View All <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingCustomerOrders.map((order) => (
                <div 
                  key={order.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 cursor-pointer transition-all"
                  onClick={() => navigate('/nas/order-approval')}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-800">{order.delivery_code}</span>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        <Clock size={10} className="mr-1" />
                        {new Date(order.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <User size={14} className="text-slate-400" />
                        {order.customer_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package size={14} className="text-slate-400" />
                        {order.items?.length || 0} items
                      </span>
                      {order.delivery_address && (
                        <span className="flex items-center gap-1 truncate max-w-[150px]">
                          <MapPin size={14} className="text-slate-400" />
                          {order.delivery_address}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-amber-700">₱{order.total_amount?.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{order.payment_method}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Low Stock Alerts - Bodega */}
      {lowStockAlerts.count > 0 && (
        <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Warehouse className="text-red-600" size={20} />
                Bodega Low Stock
                <Badge variant="secondary" className="bg-red-100 text-red-700 ml-2">
                  {lowStockAlerts.criticalCount} critical
                </Badge>
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/nas/bodega-orders')}
                className="text-red-700 border-red-300 hover:bg-red-100"
              >
                Create Order <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {lowStockAlerts.items.slice(0, 5).map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100"
                >
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">{item.name}</span>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-500">Stock: {item.currentStock} / {item.threshold}</span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          item.status === 'critical' ? 'bg-red-100 text-red-700 border-red-200' :
                          item.status === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                          'bg-blue-100 text-blue-700 border-blue-200'
                        }`}
                      >
                        {item.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-red-600">Need {item.deficit} more</p>
                    {item.supplierName && <p className="text-xs text-slate-500">{item.supplierName}</p>}
                  </div>
                </div>
              ))}
              {lowStockAlerts.count > 5 && (
                <p className="text-center text-sm text-slate-500 pt-2">
                  +{lowStockAlerts.count - 5} more items need restocking
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Low Stock Alerts - Shop */}
      {shopLowStockAlerts.count > 0 && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="text-amber-600" size={20} />
                Shop Low Stock
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 ml-2">
                  {shopLowStockAlerts.criticalCount} critical
                </Badge>
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/nas/shop')}
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
              >
                Manage Stock <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {shopLowStockAlerts.items.slice(0, 5).map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100"
                >
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">{item.name}</span>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-500">
                        W: {item.wholesaleStock || 0} | R: {item.retailStock || 0}
                      </span>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          item.status === 'critical' ? 'bg-red-100 text-red-700 border-red-200' :
                          item.status === 'warning' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                          'bg-blue-100 text-blue-700 border-blue-200'
                        }`}
                      >
                        {item.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-amber-600">Restock needed</p>
                  </div>
                </div>
              ))}
              {shopLowStockAlerts.count > 5 && (
                <p className="text-center text-sm text-slate-500 pt-2">
                  +{shopLowStockAlerts.count - 5} more items need restocking
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Recent Activity */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No recent activity</p>
            ) : (
              recentActivity.map((activity, index) => {
                const Icon = activity.icon;
                return (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      activity.type === 'order' ? 'bg-blue-100' :
                      activity.type === 'delivery' ? 'bg-green-100' :
                      activity.type === 'stock' ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                      <Icon size={16} className={
                        activity.type === 'order' ? 'text-blue-600' :
                        activity.type === 'delivery' ? 'text-green-600' :
                        activity.type === 'stock' ? 'text-red-600' : 'text-amber-600'
                      } />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{activity.message}</p>
                      <p className="text-xs text-slate-500">{activity.time}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Actions */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button 
              onClick={() => navigate('/nas/orders')}
              className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-center transition-colors"
            >
              <ShoppingCart className="mx-auto mb-2 text-blue-600" size={24} />
              <span className="text-sm font-medium text-blue-700">New Order</span>
            </button>
            <button 
              onClick={() => navigate('/nas/bodega')}
              className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-center transition-colors"
            >
              <ArrowDownRight className="mx-auto mb-2 text-green-600" size={24} />
              <span className="text-sm font-medium text-green-700">Receive Stock</span>
            </button>
            <button 
              onClick={() => navigate('/nas/deliveries')}
              className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-center transition-colors"
            >
              <Truck className="mx-auto mb-2 text-purple-600" size={24} />
              <span className="text-sm font-medium text-purple-700">New Delivery</span>
            </button>
            <button 
              onClick={() => navigate('/nas/pos')}
              className="p-4 bg-amber-50 hover:bg-amber-100 rounded-lg text-center transition-colors"
            >
              <ArrowUpRight className="mx-auto mb-2 text-amber-600" size={24} />
              <span className="text-sm font-medium text-amber-700">POS Sale</span>
            </button>
          </div>
        </CardContent>
      </Card>
      
      {/* Quick Reports */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText size={20} className="text-slate-600" />
            Quick Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <button 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/api/nas/reports/customer/daily-sales`, {
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `daily_sales_${new Date().toISOString().split('T')[0]}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Daily sales report downloaded');
                } catch (error) {
                  toast.error('Failed to download report');
                }
              }}
              className="p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-center transition-colors"
            >
              <Download className="mx-auto mb-2 text-emerald-600" size={24} />
              <span className="text-sm font-medium text-emerald-700">Daily Sales</span>
            </button>
            <button 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/api/nas/reports/customer/all-customers`, {
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Customer list downloaded');
                } catch (error) {
                  toast.error('Failed to download report');
                }
              }}
              className="p-4 bg-cyan-50 hover:bg-cyan-100 rounded-lg text-center transition-colors"
            >
              <Download className="mx-auto mb-2 text-cyan-600" size={24} />
              <span className="text-sm font-medium text-cyan-700">Customer List</span>
            </button>
            <button 
              onClick={() => navigate('/nas/reports')}
              className="p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-center transition-colors"
            >
              <FileText className="mx-auto mb-2 text-indigo-600" size={24} />
              <span className="text-sm font-medium text-indigo-700">Inventory Report</span>
            </button>
            <button 
              onClick={() => navigate('/nas/reports')}
              className="p-4 bg-pink-50 hover:bg-pink-100 rounded-lg text-center transition-colors"
            >
              <FileText className="mx-auto mb-2 text-pink-600" size={24} />
              <span className="text-sm font-medium text-pink-700">All Reports</span>
            </button>
          </div>
        </CardContent>
      </Card>
      
      {/* SMS Notifications */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare size={20} className="text-teal-600" />
            SMS Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <button 
              onClick={async () => {
                try {
                  // Get recipients with phone numbers
                  const recipientsRes = await axios.get(`${API}/api/nas/alerts/sms/recipients`);
                  const phones = recipientsRes.data.recipients
                    .filter(u => u.phone)
                    .map(u => u.phone);
                  
                  if (phones.length === 0) {
                    toast.error('No recipients with phone numbers found');
                    return;
                  }
                  
                  // Send low stock alert
                  const response = await axios.post(`${API}/api/nas/alerts/sms/low-stock-alert`, {
                    phone_numbers: phones,
                    include_urgent_only: false
                  });
                  
                  toast.success(response.data.message);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to send SMS');
                }
              }}
              className="p-4 bg-red-50 hover:bg-red-100 rounded-lg text-center transition-colors"
            >
              <AlertTriangle className="mx-auto mb-2 text-red-600" size={24} />
              <span className="text-sm font-medium text-red-700">Low Stock Alert</span>
            </button>
            <button 
              onClick={async () => {
                const cutoffDate = prompt('Enter cut-off date (e.g., Feb 25, 2026):');
                if (!cutoffDate) return;
                
                const hoursBefore = parseInt(prompt('Hours before cut-off (default: 24):') || '24', 10);
                
                try {
                  const recipientsRes = await axios.get(`${API}/api/nas/alerts/sms/recipients`);
                  const phones = recipientsRes.data.recipients
                    .filter(u => u.phone)
                    .map(u => u.phone);
                  
                  if (phones.length === 0) {
                    toast.error('No recipients with phone numbers found');
                    return;
                  }
                  
                  const response = await axios.post(`${API}/api/nas/alerts/sms/cutoff-reminder`, {
                    phone_numbers: phones,
                    cutoff_date: cutoffDate,
                    hours_before: hoursBefore
                  });
                  
                  toast.success(response.data.message);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to send SMS');
                }
              }}
              className="p-4 bg-amber-50 hover:bg-amber-100 rounded-lg text-center transition-colors"
            >
              <Clock className="mx-auto mb-2 text-amber-600" size={24} />
              <span className="text-sm font-medium text-amber-700">Cut-off Reminder</span>
            </button>
            <button 
              onClick={async () => {
                const message = prompt('Enter your message:');
                if (!message) return;
                
                try {
                  const recipientsRes = await axios.get(`${API}/api/nas/alerts/sms/recipients`);
                  const phones = recipientsRes.data.recipients
                    .filter(u => u.phone)
                    .map(u => u.phone);
                  
                  if (phones.length === 0) {
                    toast.error('No recipients with phone numbers found');
                    return;
                  }
                  
                  const response = await axios.post(`${API}/api/nas/alerts/sms/send`, {
                    phone_numbers: phones,
                    message: message
                  });
                  
                  toast.success(response.data.message);
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to send SMS');
                }
              }}
              className="p-4 bg-teal-50 hover:bg-teal-100 rounded-lg text-center transition-colors"
            >
              <Send className="mx-auto mb-2 text-teal-600" size={24} />
              <span className="text-sm font-medium text-teal-700">Custom Message</span>
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            SMS notifications are sent via Semaphore to all staff with registered phone numbers
          </p>
        </CardContent>
      </Card>
      
      {/* Scheduled SMS Alerts */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar size={20} className="text-violet-600" />
              Scheduled Alerts
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowScheduleModal(true)}
              className="text-violet-600 border-violet-200"
            >
              <Plus size={16} className="mr-1" /> Add Schedule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {scheduledAlerts.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              No scheduled alerts. Create one to send automatic SMS notifications.
            </p>
          ) : (
            <div className="space-y-2">
              {scheduledAlerts.map(alert => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={alert.is_active ? "default" : "secondary"} className={alert.is_active ? "bg-green-100 text-green-700" : ""}>
                        {alert.is_active ? 'Active' : 'Paused'}
                      </Badge>
                      <span className="font-medium text-sm capitalize">
                        {alert.alert_type.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {alert.schedule_time} • {alert.days_of_week?.slice(0, 3).map(d => d.slice(0, 3)).join(', ')}
                      {alert.days_of_week?.length > 3 && '...'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => runAlertNow(alert.id)}
                      title="Run now"
                    >
                      <Play size={14} className="text-blue-600" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => toggleScheduledAlert(alert.id, alert.is_active)}
                      title={alert.is_active ? 'Pause' : 'Activate'}
                    >
                      {alert.is_active ? <Pause size={14} className="text-amber-600" /> : <Play size={14} className="text-green-600" />}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteScheduledAlert(alert.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create Scheduled Alert Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell size={20} className="text-violet-600" />
              Create Scheduled Alert
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Alert Type</Label>
              <Select 
                value={newSchedule.alert_type} 
                onValueChange={(v) => setNewSchedule({...newSchedule, alert_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low_stock">Low Stock Alert</SelectItem>
                  <SelectItem value="daily_summary">Daily Sales Summary</SelectItem>
                  <SelectItem value="cutoff_reminder">Cut-off Reminder</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time (24-hour format)</Label>
              <Input
                type="time"
                value={newSchedule.schedule_time}
                onChange={(e) => setNewSchedule({...newSchedule, schedule_time: e.target.value})}
              />
            </div>
            <div>
              <Label>Days of Week</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const days = newSchedule.days_of_week.includes(day)
                        ? newSchedule.days_of_week.filter(d => d !== day)
                        : [...newSchedule.days_of_week, day];
                      setNewSchedule({...newSchedule, days_of_week: days});
                    }}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      newSchedule.days_of_week.includes(day)
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {day.slice(0, 3).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
            <Button onClick={createScheduledAlert} className="bg-violet-600 hover:bg-violet-700">
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASDashboard;
